// src/components/ProductCard.tsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product, getProductMainImage } from '../types';
import { useCart } from '../contexts/CartContext';
import toast from "react-hot-toast";
import { useDeliveryDate } from '../hooks/useDeliveryDate';
import { ShoppingCartIcon } from "@heroicons/react/16/solid";
import { useAuth } from "../contexts/AuthContext";
import {MarqueeText} from "./MarqueeText";
import {PriceDisplay} from "../components/PriceDisplay";



interface ProductCardProps {
    product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const { user } = useAuth();
    const [isAddingToCart, setIsAddingToCart] = useState(false);

    // Находим вариант с минимальной ценой
    const cheapestVariant = useMemo(() => {
        if (!product.variants || product.variants.length === 0) {
            return null;
        }
        return product.variants.reduce((cheapest, current) => {
            return (current.price < cheapest.price) ? current : cheapest;
        }, product.variants[0]);
    }, [product.variants]);

    const { deliveryDate, loading: deliveryLoading } = useDeliveryDate(user?.id);

    const stock = cheapestVariant ? cheapestVariant.quantity : (product.stock_quantity || 0);
    const price = cheapestVariant ? cheapestVariant.price : 0;

    const mainImage = getProductMainImage(product);
    const formattedPrice = price ? price.toLocaleString() : '0';

    const handleAddToCart = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isAddingToCart) return;

        if (product && product.id && cheapestVariant) {
            setIsAddingToCart(true);
            await addToCart(product.id, cheapestVariant.id, 1);
            setTimeout(() => {
                setIsAddingToCart(false);
            }, 1500);
        } else {
            toast.error('Товар недоступен для добавления');
            setIsAddingToCart(false);
        }
    };

    if (!product || !product.id) {
        return null;
    }

    const isInStock = stock > 0;
    const isOutOfStock = stock === 0;
    const variantColor = cheapestVariant?.color || product.color;
    const variantSize = cheapestVariant?.size_or_volume;

    return (
        <div
            onClick={() => navigate(`/product/${product.id}`)}
            className="product-card-wb"
        >
            <div className="product-image-wb">
                <img
                    src={mainImage}
                    alt={product.name || 'Товар'}
                    loading="lazy"
                />
                {stock > 0 && (
                    <div className="stock-badge-wb">
                        {stock} шт.
                    </div>
                )}
                {isOutOfStock && (
                    <div className="stock-badge-wb out-of-stock">
                        Нет в наличии
                    </div>
                )}
            </div>

            <div className="product-info-wb">
                {product.brand && (
                    <div className="product-brand-wb">{product.brand}</div>
                )}

                {/* ✅ Используем компонент MarqueeText */}
                <MarqueeText
                    text={product.name || 'Без названия'}
                    className="product-name-wb"
                    speed={12}



                />

                <div className="product-specs-wb">
                    {variantColor && (
                        <span className="product-spec-wb">{variantColor}</span>
                    )}
                    {variantSize && (
                        <span className="product-spec-wb">{variantSize}</span>
                    )}
                </div>

                {/* <div className="product-price-wb">
                    <span className="price-current-wb">{formattedPrice} TJS</span>
                </div>*/}

                {/* ✅ ЦЕНА С КОНВЕРТЕРОМ ВАЛЮТ */}
                <div className="price-section-wb" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>

                    <div onClick={(e) => e.stopPropagation()}>
                        <PriceDisplay
                            priceInTJS={price}
                            size="14px"
                            buttonSize="8px"
                            showButtonFlag={false}
                        />
                    </div>
                </div>

                <button
                    onClick={handleAddToCart}
                    disabled={isOutOfStock || !product.is_available || isAddingToCart}
                    className={`delivery-button ${isAddingToCart ? 'adding' : ''}`}
                >
                    <div className="delivery-info">
                        <span className="delivery-icon">📦</span>
                        <div className="delivery-text">
                            <span className="delivery-label">Доставка</span>
                            {!deliveryLoading && !isAddingToCart && (
                                <span className="delivery-date">{deliveryDate}</span>
                            )}
                            {deliveryLoading && !isAddingToCart && (
                                <span className="delivery-date loading">...</span>
                            )}
                            {isAddingToCart && (
                                <span className="delivery-date added">✓ Добавлено!</span>
                            )}
                        </div>
                    </div>
                    {isInStock && !isAddingToCart && (
                        <div className="cart-action">
                            <ShoppingCartIcon className="cart-icon" />
                            <span>В корзину</span>
                        </div>
                    )}
                    {isOutOfStock && (
                        <div className="cart-action disabled">
                            <span>Нет в наличии</span>
                        </div>
                    )}
                    {isAddingToCart && (
                        <div className="cart-action adding">
                            <span className="spinner"></span>
                        </div>
                    )}
                </button>
            </div>
        </div>
    );
};

export default ProductCard;